import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { languageCodes, languages, defaultLanguage } from '../i18n';

const SITE_URL = 'https://fxastra.com';

// Component to handle language-based routing and SEO
export default function LanguageRouter() {
  const { lang } = useParams();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Validate and set language from URL
  useEffect(() => {
    if (lang && languageCodes.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
    } else if (lang && !languageCodes.includes(lang)) {
      // Invalid language code, redirect to default
      const newPath = location.pathname.replace(`/${lang}`, '') || '/';
      navigate(newPath, { replace: true });
    }
  }, [lang, i18n, navigate, location.pathname]);

  // Get current path without language prefix
  const getPathWithoutLang = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    if (pathParts[0] && languageCodes.includes(pathParts[0])) {
      return '/' + pathParts.slice(1).join('/');
    }
    return location.pathname;
  };

  const currentPath = getPathWithoutLang();
  const currentLang = lang || defaultLanguage;

  // Generate hreflang URLs for all languages
  const generateHreflangUrls = () => {
    return languages.map(language => {
      const langPath = language.code === defaultLanguage 
        ? currentPath 
        : `/${language.code}${currentPath === '/' ? '' : currentPath}`;
      return {
        hreflang: language.hreflang,
        href: `${SITE_URL}${langPath}`
      };
    });
  };

  const hreflangUrls = generateHreflangUrls();
  const canonicalUrl = currentLang === defaultLanguage 
    ? `${SITE_URL}${currentPath}` 
    : `${SITE_URL}/${currentLang}${currentPath === '/' ? '' : currentPath}`;

  return (
    <>
      <Helmet>
        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Hreflang tags for all languages */}
        {hreflangUrls.map(({ hreflang, href }) => (
          <link key={hreflang} rel="alternate" hreflang={hreflang} href={href} />
        ))}
        
        {/* x-default for search engines */}
        <link rel="alternate" hreflang="x-default" href={`${SITE_URL}${currentPath}`} />
        
        {/* Set HTML lang attribute */}
        <html lang={currentLang} />
      </Helmet>
      <Outlet />
    </>
  );
}

// Hook to get language-aware links
export function useLanguageLink() {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const getLocalizedPath = (path) => {
    if (currentLang === defaultLanguage) {
      return path;
    }
    return `/${currentLang}${path === '/' ? '' : path}`;
  };

  return { getLocalizedPath, currentLang };
}

// Component for language-aware Link
export function LocalizedLink({ to, children, ...props }) {
  const { getLocalizedPath } = useLanguageLink();
  const localizedTo = getLocalizedPath(to);
  
  return (
    <a href={localizedTo} {...props}>
      {children}
    </a>
  );
}
